import * as Sentry from '@sentry/react' // TODO: import only necessary functions
import { detect } from 'detect-browser'
import snakeCase from 'lodash/snakeCase'

import request from 'common/utils/request'
import { getSocialMediaClickIds } from 'artkive/components/Tracker/utils'
import { BOX_PRODUCT_TYPE } from 'artkive/stores/product.constants'
import { DEFAULT_CURRENCY_CODE } from 'artkive/utils/constants'
import {
  addShopperConversion,
  awinConversion,
  giddyUpInitiateCheckout,
  giddyUpPageLoad,
  giddyUpPurchase,
  tripleWhaleAddToCart,
  tripleWhaleCheckoutStarted,
  tripleWhaleContact,
  tripleWhalePageLoad,
  tripleWhalePurchase,
} from 'artkive/utils/externalTracking'
import handleOncePerSession from 'artkive/utils/handleOncePerSession'
import { validateEmail } from 'artkive/utils/validations'

const browser = detect()

const TRACKING_META =
  typeof window === 'undefined'
    ? {}
    : {
        browserType: browser.type,
        deviceOs: browser.os,
        env: window.app.envType,
        trackingCategory: 'ecom',
      }

const getTrackingMeta = () => ({ ...getSocialMediaClickIds(), ...TRACKING_META })

const handleError = (err, msg = 'Error tracking event failed') => {
  console.error(msg, JSON.stringify(err))

  Sentry.withScope(function (scope) {
    // group errors together based on their request and response
    scope.setFingerprint(['analytics', err.name])
    Sentry.captureException(err)
  })

  return err
}

/**
 *
 * @param {function} func callback that should be executed if tracking is enabled
 * @param {function} callback callback that should be executed after tracking regardless environment
 */
const handleTracking = async (func, callback) => {
  if (window && window.app.envName !== 'test' && !window.app.admin && browser.type === 'browser') {
    if (!analytics || analytics.loadFailure) {
      handleError(new Error('Error: analytics is not present or initialized'))
    } else {
      try {
        await func()
      } catch (err) {
        handleError(err)
      }
    }
  }

  typeof callback === 'function' && (await callback())

  return null
}

const priceMeta = ({ amount, currency = DEFAULT_CURRENCY_CODE }) => ({
  amount,
  currency,
  price: amount,
  value: amount,
})

/*
dispatches both analytics and api post requests
*/
export const captureEmail = ({
  email,
  phone,
  firstName,
  lastName,
  formType,
  kind,
  product,
  productId = null,
  formId,
  step,
}) =>
  handleTracking(
    async () => {
      const eventName = 'email capture'
      if (email) {
        if (!validateEmail(email)) return console.warn(`Error: email of '${email}' is invalid`)

        identifyUser({ email, name: [firstName, lastName].filter(Boolean).join(' ') })

        analytics.track(eventName, { email, formType, product, productId, formId, step, ...getTrackingMeta() })
      }

      // For tracking SMS Klaviyo Forms
      if (phone) {
        analytics.track(eventName, { email, formType, product, productId, formId, step, ...getTrackingMeta() })
      }
    },
    () => {
      if (!validateEmail(email)) return

      return request
        .post(Routing.api_v1_create_new_marketing_list_item(), {
          email,
          phone,
          kind,
          first_name: firstName,
          last_name: lastName,
        })
        .catch((err) => console.warn('Error: could not save marketing email', err))
    },
  )

const aliasAnonymousUser = (userId) => {
  const user = analytics.user?.()
  if (user && !user.id?.() && userId) {
    analytics.alias(userId, user.anonymousId())
  }
}

export const identifyUser = ({ email, name, ...rest }) =>
  handleTracking(() => {
    if (!validateEmail(email)) return console.warn(`Error: email of '${email}' is invalid`)

    tripleWhaleContact({ email })
    aliasAnonymousUser(email)
    analytics.identify(email, {
      email,
      name,
      ...rest,
      ...getTrackingMeta(),
    })
  })

const specialProductEventPrefix = (productType) => (productType !== BOX_PRODUCT_TYPE ? `${productType}: ` : '')

export const initiateCheckout = ({
  amount,
  discount,
  email,
  name,
  product,
  promo,
  productId = null,
  currency = DEFAULT_CURRENCY_CODE,
}) =>
  handleTracking(() => {
    const pricingMeta = priceMeta({ amount, currency })
    const eventName = `${specialProductEventPrefix(product)}initiate checkout`

    handleOncePerSession(product, productId, () => {
      tripleWhaleAddToCart({ productId })
      tripleWhaleCheckoutStarted({ productId })
      giddyUpInitiateCheckout({ productId })
      analytics.track(eventName, {
        discount,
        email,
        name,
        product,
        productId,
        promo,
        ...pricingMeta,
        ...getTrackingMeta(),
      })
    })
  })

export const page = ({ ...attrs } = {}) =>
  handleTracking(() => {
    tripleWhalePageLoad()
    analytics.page('ecom', { ...attrs, ...getTrackingMeta() })
  })

// NOTE: should be invoked before server side tracking so FB can deduplicate events
export const purchase = (
  {
    amount,
    discount,
    productId,
    name,
    orderId,
    product,
    promo,
    paymentKind,
    quantity,
    tracking = {},
    information = {},
    context = {},
    currency = DEFAULT_CURRENCY_CODE,
  },
  callback,
) =>
  handleTracking(async () => {
    const pricingMeta = priceMeta({ amount, currency })
    const eventName = `${specialProductEventPrefix(product)}purchase`
    const email = information.email

    // NOTE: some integrations require specific events
    if (product === BOX_PRODUCT_TYPE) {
      analytics.track(
        'Order Completed',
        { orderId, total: amount, amount },
        { integrations: { All: false, ShareASale: true } },
      )
    }

    try {
      tripleWhalePurchase(context.messageId, {
        email,
        name,
        orderId,
        quantity,
        productId,
        address: information,
      })
    } catch (error) {
      Sentry.captureException(error)
    }

    return analytics.track(
      eventName,
      {
        discount,
        email,
        name,
        orderId,
        product,
        promo,
        productId,
        paymentKind,
        utmSource: tracking.utmSource,
        utmCampaign: tracking.utmCampaign,
        utmMedium: tracking.utmMedium,
        utmContent: tracking.utmContent,
        // NOTE: required by Google AdWords New integration
        total: amount,
        order_id: orderId,
        // NOTE: Bing Ads UAT tag reads conversion value from `revenue`
        revenue: amount,
        ...pricingMeta,
        ...getTrackingMeta(),
      },
      { ...context },
    )
  }, callback)

// initial tracking. should only be used once per react render
export const load = ({ user, ...attrs }) =>
  handleTracking(() => {
    analytics.page('ecom', { ...attrs, ...getTrackingMeta() })
    giddyUpPageLoad({ productId: attrs.productId })

    if (typeof user === 'object' && Object.keys(user).length > 1 && !user.admin) {
      aliasAnonymousUser(user.email)

      analytics.identify(user.email, {
        email: user.email,
        name: user.name,
        uuid: user.uuid,
      })
    }
  })

// Tracks given event
export const action = (event, options) => {
  if (!event) {
    console.warn('No event specified for track action')
    return
  }
  console.info(`%c tracker event: ${event}`, 'background: #222; color: #bada55', options)

  return handleTracking(() =>
    analytics.track(event, {
      ...getTrackingMeta(),
      env: process.env.AWS_ENV,
      ...options,
    }),
  )
}

export const trackConversion = ({ orderId, total, email, currency, tracking, promoCode, productId }) =>
  handleTracking(() => {
    addShopperConversion({ orderId, value: total, email, currency })
    awinConversion({ orderId, total, tracking, promoCode, currency })
    giddyUpPurchase({ orderId, total, productId })
  })

export const extractUtmParameters = (params) => {
  const source = params.get('utm_source')
  if (!source) return null

  return {
    utmSource: source,
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
    utmTerm: params.get('utm_term'),
  }
}

export const exportUtmParameters = (params) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    acc[snakeCase(key)] = value
    return acc
  }, {})

export const generateEventId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}
